// VIPs (global/vips.md), pure parts. The document is Markdown: a heading and a few lines of
// explanation, then one address or domain per line. The editor shows only those lines; saving
// puts them back under the document's own heading. Reading mirrors the server's parser
// (server/notify/breakthrough.ts parseVips) so what is shown here is what breaks through.

export const VIPS_DOC_KEY = "global/vips.md";

export const DEFAULT_VIPS_HEAD = `# VIPs

People whose mail should break through the batch and reach you now, one per line: an email
address or a domain. Lines starting with # are ignored.`;

const ADDRESS_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/;
const DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/;
const BULLET_RE = /^\s*[-*]\s*/;
const PLACEHOLDER_RE = /^\s*[-*]\s*\(.*\)\s*$/;

export type VipLine =
  { kind: "ignored" } | { kind: "address" | "domain"; value: string } | { kind: "unknown" };

/** How the server reads one line of the document. */
export function readVipLine(raw: string): VipLine {
  const line = raw.replace(BULLET_RE, "").trim().toLowerCase();
  if (!line || line.startsWith("#") || line.startsWith("(")) return { kind: "ignored" };
  const address = ADDRESS_RE.exec(line)?.[0];
  if (address) return { kind: "address", value: address };
  const token = (line.split(/\s+/)[0] ?? "").replace(/[<>,;]/g, "");
  if (DOMAIN_RE.test(token)) return { kind: "domain", value: token };
  return { kind: "unknown" };
}

export interface VipDocument {
  /** Everything that is not an entry (heading, explanation, comments). */
  head: string;
  /** Entry lines as written, without the bullet. */
  entries: string[];
}

export function splitVipDocument(content: string): VipDocument {
  const head: string[] = [];
  const entries: string[] = [];
  for (const raw of content.split("\n")) {
    const read = readVipLine(raw);
    if (read.kind === "address" || read.kind === "domain") {
      entries.push(raw.replace(BULLET_RE, "").trim());
    } else {
      head.push(raw);
    }
  }
  return { head: head.join("\n").trim(), entries };
}

export interface VipCheck {
  /** Recognized lines, as written, first occurrence only. */
  entries: string[];
  addresses: number;
  domains: number;
  /** Lines MeeMail would not understand (1-based line numbers in the editor). */
  problems: { line: number; text: string }[];
  duplicates: string[];
}

/** Check the editor's text: one address or domain per line. */
export function checkVipLines(text: string): VipCheck {
  const out: VipCheck = { entries: [], addresses: 0, domains: 0, problems: [], duplicates: [] };
  const seen = new Set<string>();
  text.split("\n").forEach((raw, i) => {
    // "@example.com" means the domain; the server wants it without the @.
    const written = raw
      .replace(BULLET_RE, "")
      .trim()
      .replace(/^@(?=[^@\s]+$)/, "");
    if (!written) return;
    const read = readVipLine(written);
    if (read.kind === "address" || read.kind === "domain") {
      if (seen.has(read.value)) {
        out.duplicates.push(read.value);
        return;
      }
      seen.add(read.value);
      out.entries.push(written);
      if (read.kind === "address") out.addresses++;
      else out.domains++;
    } else if (read.kind === "unknown") {
      out.problems.push({ line: i + 1, text: written });
    }
  });
  return out;
}

/** The document to save: its own heading and explanation, then the entries as a list. */
export function joinVipDocument(head: string, entries: readonly string[]): string {
  const lines = (head.trim() || DEFAULT_VIPS_HEAD).split("\n");
  const kept = entries.length ? lines.filter((l) => !PLACEHOLDER_RE.test(l)) : lines;
  const top = kept.join("\n").trim();
  if (!entries.length) return `${top}\n`;
  return `${top}\n\n${entries.map((e) => `- ${e}`).join("\n")}\n`;
}

/** The document with `addresses` added after the entries already there, each listed once. */
export function addVipEntries(content: string, addresses: readonly string[]): string {
  const doc = splitVipDocument(content);
  const check = checkVipLines([...doc.entries, ...addresses].join("\n"));
  return joinVipDocument(doc.head, check.entries);
}

/** The document without the entries for `address` (a domain entry covering it stays). */
export function removeVipAddress(content: string, address: string): string {
  const target = address.trim().toLowerCase();
  const doc = splitVipDocument(content);
  const kept = doc.entries.filter((entry) => {
    const read = readVipLine(entry);
    return !(read.kind === "address" && read.value === target);
  });
  return joinVipDocument(doc.head, kept);
}

/** Why `address` is a VIP: listed itself, covered by its domain, or not at all. */
export type VipMatch = { kind: "address" } | { kind: "domain"; domain: string } | { kind: "none" };

export function vipMatch(content: string, address: string): VipMatch {
  const target = address.trim().toLowerCase();
  const domain = target.slice(target.lastIndexOf("@") + 1);
  let byDomain = false;
  for (const raw of content.split("\n")) {
    const read = readVipLine(raw);
    if (read.kind === "address" && read.value === target) return { kind: "address" };
    if (read.kind === "domain" && read.value === domain) byDomain = true;
  }
  return byDomain ? { kind: "domain", domain } : { kind: "none" };
}

export function vipSummary(check: Pick<VipCheck, "addresses" | "domains">): string {
  const parts = [
    check.addresses ? `${check.addresses} ${check.addresses === 1 ? "person" : "people"}` : "",
    check.domains ? `${check.domains} ${check.domains === 1 ? "domain" : "domains"}` : "",
  ].filter(Boolean);
  return parts.length ? `${parts.join(" and ")} break through.` : "Nobody breaks through yet.";
}
